import { faBox, faCheckCircle, faChevronRight, faClock, faExclamationTriangle, faInfoCircle, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { Dashboard } from 'appDtos';
import "chart.js/auto";
import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, ListGroup, Row } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { DashboardPaths } from '../../shared/api/constants';
import { AppPaths } from '../../shared/appConstants';
import { AlertComponent, Icon, Loading } from '../../shared/components';
import { useApi } from '../../shared/hooks/useApi';
import { useApiHandleError } from '../../shared/hooks/useApiHandleError';
import { KpiCard } from './KpiCard';

export function ChartsComponent() {
  const API = useApi();
  const { setError, errorMessage } = useApiHandleError();
  const goTo = useNavigate();
  const [dashboard, setDashboard] = useState<Dashboard>();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (errorMessage) setError('');
    (async () => {
      try {
        const response = await API.get(DashboardPaths.GetDashboard());
        setDashboard(response.data);
      } catch (error) {
        setError(error)
      }
      setLoading(false);
      setRefresh(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const statuses = {
    labels: dashboard?.cards.filter(c => c.entityType === 'status').map(c => c.title) ?? [],
    datasets: [{
      label: 'Statuses',
      data: dashboard?.cards.filter(c => c.entityType === 'status').map(c => c.count) ?? [],
      backgroundColor: [
        '#7db67fff',  // Active - Green
        '#455566ff',  // Disposed - Dark Gray
        '#71b8f2ff',  // Registration Pending - Blue
        '#f1cc5dff',  // Update Pending - Yellow
        '#c24dd6ff',  // Transfer Pending - Purple
        '#f46d64ff',  // Disposal Pending - Red
      ],
      borderWidth: 0,
      borderRadius: 4
    }]
  }

  const conditions = {
    labels: dashboard?.cards.filter(c => c.entityType === 'condition').map(c => c.title) ?? [],
    datasets: [{
      label: 'Asset Conditions',
      data: dashboard?.cards.filter(c => c.entityType === 'condition').map(c => c.count) ?? [],
      backgroundColor: [
        '#7db67fff',  // Good - Green
        '#f1cc5dff',  // Fair - Yellow
        '#f46d64ff',  // Damaged - Red
      ],
      borderWidth: 0
    }]
  }

  const handleChartClick = (entityType: string) => (e: any) => {
    const data = dashboard?.cards.find(c => c.entityType === entityType && c.title === e.chart.tooltip.title[0]);
    return data ? goTo(`${AppPaths.getSearchAndFilter()}/?${data?.entityType}=${data?.title}`) : false;
  }

  const statusesOpts = {
    onClick: handleChartClick('status'),
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  }

  const conditionsOpts = {
    onClick: handleChartClick('condition'),
    responsive: true,
    maintainAspectRatio: false,
  }

  const totalAssets = dashboard?.cards.find(c => c.entityType === 'all');

  // Calculate KPI values
  const totalAssetsCount = totalAssets?.count ?? 0;
  const activeCount = dashboard?.cards.find(c => c.entityType === 'status' && c.title === 'Active')?.count ?? 0;
  const pendingCount = dashboard?.cards
    .filter(c => c.entityType === 'status' && c.title.includes('Pending'))
    .reduce((sum, card) => sum + card.count, 0) ?? 0;
  const damagedCount = dashboard?.cards.find(c => c.entityType === 'condition' && c.title === 'Damaged')?.count ?? 0;

  return (
    <Container fluid className="px-3 px-lg-4" style={{ backgroundColor: '#F2F6F9', minHeight: '100vh' }}>
      <div className="page-header my-2 my-md-3 p-2 p-md-3 fw-bold rounded border border-1 border-success text-success d-flex flex-row justify-content-between align-items-center">
        <h1 className="h5 h3-md mb-0">Dashboard</h1>
        <Button
          className="fw-bold"
          title="Refresh dashboard"
          disabled={loading}
          onClick={() => setRefresh(true)}
          variant="outline-success"
          aria-label={loading ? 'Loading dashboard data' : 'Refresh dashboard'}
        >
          <Icon className={loading ? 'refreshing' : ''} icon={faRefresh} aria-hidden="true" />
          <span className="d-none d-md-inline ms-2">{loading ? 'Loading' : 'Refresh'}</span>
        </Button>
      </div>
      <AlertComponent message={errorMessage} />

      {/* KPI Summary Section */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <KpiCard
            title="Total Assets"
            value={totalAssetsCount}
            icon={faBox}
            variant="primary"
            onClick={() => goTo(AppPaths.getSearchAndFilter())}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KpiCard
            title="Active Assets"
            value={activeCount}
            icon={faCheckCircle}
            variant="success"
            onClick={() => goTo(`${AppPaths.getSearchAndFilter()}/?status=Active`)}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KpiCard
            title="Pending Approval"
            value={pendingCount}
            icon={faClock}
            variant="warning"
            onClick={() => goTo(AppPaths.getRegistrations())}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KpiCard
            title="Damaged Assets"
            value={damagedCount}
            icon={faExclamationTriangle}
            variant="danger"
            onClick={() => goTo(`${AppPaths.getSearchAndFilter()}/?condition=Damaged`)}
          />
        </Col>
      </Row>

      <div className="dashboard-chart-container">
        <Card className="border shadow-sm border-success chart-card-recent" style={{ overflow: 'hidden' }}>
          <Card.Body className='p-3 p-md-4 d-flex flex-column' style={{ height: '100%', overflow: 'hidden' }}>
            <Card.Title className="mb-2 mb-md-3 fw-bold d-flex align-items-center">
              <span className="me-2" style={{ color: '#00796B', fontSize: '1.25rem' }}>
                <Icon icon={faClock} />
              </span>
              Recent Activities
            </Card.Title>
            {loading ? <Loading /> : (
              dashboard?.recentActivities && dashboard.recentActivities.length > 0 ? (
                <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
                  <ListGroup variant="flush" className="recent-activities-list">
                    {dashboard.recentActivities.map((activity, i) => (
                      <ListGroup.Item
                        action
                        key={i}
                        onClick={() => goTo(AppPaths.getAssetDetail(activity.assetId))}
                        className="px-0 py-2 border-bottom"
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          overflow: 'hidden'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ minWidth: 0 }}>
                          <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem', flex: 1, marginRight: '8px', minWidth: 0 }}>
                            {activity.value}
                          </div>
                          <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                            <small className="text-muted me-2" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {activity.date}
                            </small>
                            <span style={{ color: '#999', fontSize: '12px' }}>
                              <Icon icon={faChevronRight} />
                            </span>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              ) : (
                <div className="text-center text-muted d-flex flex-column justify-content-center align-items-center" style={{ flex: 1 }}>
                  <div style={{ fontSize: '2rem', opacity: 0.3 }} className="mb-2">
                    <Icon icon={faInfoCircle} />
                  </div>
                  <p className="mb-0">No recent activities</p>
                </div>
              )
            )}
          </Card.Body>
        </Card>
        <Card className="border shadow-sm border-success chart-card-conditions">
          <Card.Body className='p-3 p-md-4 d-flex flex-column' style={{ height: '100%' }}>
            <Card.Title className="mb-2 fw-bold">Conditions</Card.Title>
            <div className="chart-wrapper" style={{ flex: 1, minHeight: '200px', position: 'relative' }}>
              {loading ? <Loading /> : <Pie data={conditions} options={conditionsOpts} />}
            </div>
          </Card.Body>
        </Card>
        <Card className="border shadow-sm border-success chart-statuses chart-card-statuses">
          <Card.Body className='p-3 p-md-4 d-flex flex-column' style={{ height: '100%' }}>
            <Card.Title className="mb-2 fw-bold">Statuses</Card.Title>
            <div className="chart-wrapper" style={{ flex: 1, minHeight: '200px', position: 'relative' }}>
              {loading ? <Loading /> : <Bar data={statuses} options={statusesOpts} />}
            </div>
          </Card.Body>
        </Card>
      </div>
      <div className="dashboard-list-container">
        {dashboard?.lists.map((l, i) => (
          <Card key={i} className="list-card border shadow-sm border-success">
            <Card.Subtitle className="list-card-title">{l.title}</Card.Subtitle>
            <Card.Body>
              {loading ? <Loading /> : <ListGroup key={i} className="list-group">
                {l.data.map(d => (
                  <ListGroup.Item
                    action
                    onClick={() => goTo(`${AppPaths.getSearchAndFilter()}/?${d.entityType}=${d.title}`)}
                    key={d.title}
                    className="d-flex justify-content-between align-items-center">
                    <span>{d.title}</span><span className="fw-bold">{d.count}</span>
                  </ListGroup.Item>))}
              </ListGroup>}
            </Card.Body>
          </Card>
        ))}
      </div>
    </Container>
  )
}
